import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Stack from '@mui/material/Stack';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, type Control } from 'react-hook-form';

import { type OfferFormValues, type OfferTextFieldName } from '@/domain/offers/types';

interface Props extends Omit<TextFieldProps, 'name' | 'label' | 'error'> {
  name: OfferTextFieldName;
  control: Control<OfferFormValues>;
  label: string;
  optionalLabel?: string;
  hint?: string;
  /** Shows "38/60" next to the label. */
  maxChars?: number;
}

/** Labelled, controlled text input with the field error underneath and an optional counter. */
const OfferFormField = ({
  name,
  control,
  label,
  optionalLabel,
  hint,
  maxChars,
  ...textFieldProps
}: Props) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <FormControl fullWidth>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
          <FormLabel htmlFor={name}>
            {label}
            {optionalLabel ? (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 0.75 }}
              >
                {optionalLabel}
              </Typography>
            ) : null}
          </FormLabel>
          {maxChars ? (
            <Typography
              variant="caption"
              color={String(field.value ?? '').length > maxChars ? 'error' : 'text.secondary'}
            >
              {String(field.value ?? '').length}/{maxChars}
            </Typography>
          ) : null}
        </Stack>
        <TextField
          {...textFieldProps}
          {...field}
          id={name}
          fullWidth
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? hint}
        />
      </FormControl>
    )}
  />
);

export default OfferFormField;
